import type { LinkProps } from "@chakra-ui/core";
import { Box, Divider, Link as ChakraLink, Text } from "@chakra-ui/core";
import { Li, Ol, Ul } from "atoms/lists";
import { fonts } from "lib/theme";
import { colors } from "lib/theme/colors";
import type { RichTextBlock } from "prismic-reactjs";
import { Elements, RichText as PrismicRichText } from "prismic-reactjs";
import React, { useMemo } from "react";

export const monoProps = {
  fontFamily: fonts.mono,
  letterSpacing: "wider",
  fontWeight: "semibold",
};

const PenfoldText = ({ mono = false, ...props }) => {
  const mergedProps = { ...props, ...(mono && monoProps) };
  return <Text {...mergedProps} />;
};
PenfoldText.defaultProps = {
  fontFamily: fonts.body,
  color: "navy.500",
  mt: 0,
  mb: 0,
};
export { PenfoldText as Text };

/**
 * Headings
 */

export const PenfoldHeading = (props) => <PenfoldText {...props} />;
PenfoldHeading.defaultProps = {
  fontFamily: fonts.heading,
  color: colors.navy[500],
  fontWeight: "bold",
  wordBreak: "normal",
};

export const H0 = (props) => <PenfoldHeading {...props} />;
H0.defaultProps = {
  fontSize: "48px",
  lineHeight: 58 / 48,
  mb: 8,
};

export const H1 = (props) => <PenfoldHeading {...props} as="h1" />;
H1.defaultProps = {
  // TODO: this breaks the <Heading /> component
  fontSize: ["42px", , "4xl"],
  lineHeight: [42 / 42, , 40 / 36],
  mb: 7,
};

export const H2 = (props) => <PenfoldHeading {...props} as="h2" />;
H2.defaultProps = {
  fontSize: "3xl",
  lineHeight: 38 / 28,
  mb: 6,
};

export const H3 = (props) => <PenfoldHeading {...props} as="h3" />;
H3.defaultProps = {
  fontSize: "2xl",
  lineHeight: 28 / 24,
  mb: 5,
};

export const H4 = (props) => <PenfoldHeading {...props} as="h4" />;
H4.defaultProps = {
  fontSize: "xl",
  lineHeight: 26 / 20,
  mb: 4,
};

/**
 * The 2021 rebrand typography doesn't go as low as H5, but sometimes H4 is too big.
 * This H5 reuses the styles from Bold Para until we have a dedicated H5
 */
export const H5 = (props) => <PenfoldHeading {...props} as="h5" />;
H5.defaultProps = {
  fontSize: "md",
  lineHeight: 22 / 16,
  letterSpacing: "normal",
  mb: 3,
};

export const ContentSectionHeader = (props) => (
  <PenfoldHeading {...props} as="h5" />
);
ContentSectionHeader.defaultProps = {
  fontFamily: fonts.body,
  fontSize: "xs",
  fontWeight: 600,
  lineHeight: 1 / 12,
  mb: 4,
};
export const AltContentSectionHeader = (props) => (
  <PenfoldHeading {...props} as="h5" />
);
AltContentSectionHeader.defaultProps = {
  fontFamily: fonts.body,
  fontSize: "xs",
  fontWeight: 800,
  lineHeight: 1 / 12,
  mb: 4,
};

const headerLevelsPropsMapping = {
  h1: H1.defaultProps,
  h2: H2.defaultProps,
  h3: H3.defaultProps,
  h4: H4.defaultProps,
  h5: H5.defaultProps,
};

const headerLevelsProps = (levels) => ({
  fontSize: levels.map((level) => headerLevelsPropsMapping[level].fontSize),
  lineHeight: levels.map((level) => headerLevelsPropsMapping[level].lineHeight),
  mb: levels.map((level) => headerLevelsPropsMapping[level].mb),
});

// TODO: Refactor to disambiguate PenfoldHeading vs. Heading
// Make H1, H2... levels extend the resultant heading component with defaults.
export const Heading = ({ levels, ...props }) => (
  <PenfoldHeading {...headerLevelsProps(levels)} {...props} />
);

/**
 * Paragraphs
 */

export const PLarge = (props) => <P {...props} />;
PLarge.defaultProps = {
  fontSize: "lg",
};

export const PBold = (props) => <P {...props} />;
PBold.defaultProps = {
  fontFamily: fonts.heading,
  fontWeight: "bold",
};

export const PBoldAlt = (props) => <P {...props} />;
PBoldAlt.defaultProps = {
  fontWeight: "bold",
};

export const P = (props) => <PenfoldText {...props} />;
P.defaultProps = {
  as: "p",
  fontSize: "md",
  lineHeight: 22 / 16,
  mb: 6,
};

export const A = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (props, ref) => <ChakraLink ref={ref} {...props} />
);
export const AStyles = {
  fontWeight: "bold",
  lineHeight: 22 / 16,
  fontFamily: fonts.body,
  color: colors.navy[500],
  cursor: "pointer",
  _hover: {
    color: "pink.500",
  },
  _active: {
    color: "pink.500",
  },
};
A.defaultProps = AStyles;

export const PSmallBold = (props) => <PSmall {...props} />;
PSmallBold.defaultProps = {
  fontWeight: "bold",
  fontFamily: fonts.heading,
};

export const PSmall = (props) => <P {...props} />;
PSmall.defaultProps = {
  fontSize: "sm",
  lineHeight: 20 / 14,
  mb: 5,
};

export const ASmall = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (props, ref) => <A {...props} ref={ref} />
);
ASmall.defaultProps = {
  fontSize: "sm",
  textDecoration: "underline",
};

export const PFine = (props) => <P {...props} />;
PFine.defaultProps = {
  fontSize: "xs",
  lineHeight: 16 / 12,
  mb: 5,
};

export const ParagraphLead = (props) => <PenfoldHeading {...props} as="p" />;
ParagraphLead.defaultProps = {
  fontSize: "32px",
  fontWeight: "bold",
  lineHeight: 38 / 32,
  mb: 7,
};

const paragraphPropsMapping = {
  pLarge: PLarge.defaultProps,
  p: P.defaultProps,
  pSmall: PSmall.defaultProps,
  pFine: PFine.defaultProps,
};

const paragraphLevelsProps = (levels) => ({
  fontSize: levels.map((level) => paragraphPropsMapping[level].fontSize),
  lineHeight: levels.map((level) => paragraphPropsMapping[level].lineHeight),
  mb: levels.map((level) => paragraphPropsMapping[level].mb),
});

// TODO: Similar refactor to the header, see Heading vs. PenfoldHeading
export const Paragraph = ({ levels, ...props }) => (
  <PenfoldText {...paragraphLevelsProps(levels)} {...props} />
);

// TODO: Update this when label is defined by design
export const Label = (props) => <PenfoldText as="label" {...props} />;
Label.defaultProps = {
  fontSize: "sm",
};

export const UnstyledA = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (props, ref) => <ChakraLink ref={ref} {...props} />
);
UnstyledA.defaultProps = {
  color: "navy.500",
  display: "inline-block",
  textDecoration: "none",
  _hover: {
    textDecoration: "none",
  },
};

export const Strong = (props) => (
  <Box as="strong" fontWeight="bold" {...props} />
);

export const NonBreakingSpan = ({ children }) => (
  <Box as="span" whiteSpace="nowrap">
    {children}
  </Box>
);

export const Hr = (props) => <Divider {...props} />;
Hr.defaultProps = {
  color: "gray.500",
  my: 8,
};

export const Pink = (props) => <Box as="span" {...props} />;
Pink.defaultProps = {
  color: "pink.500",
};

export const PRISMIC_ELEMENTS = {
  [Elements.paragraph]: P,
  [Elements.heading1]: H1,
  [Elements.heading2]: H2,
  [Elements.heading3]: H3,
  [Elements.heading4]: H4,
  [Elements.heading6]: H0,
  [Elements.list]: Ul,
  [Elements.listItem]: Li,
  [Elements.oList]: Ol,
  [Elements.oListItem]: Li,
};

const stripPenfoldDomain = (url: string) => {
  return url.replace(/^https:\/\/getpenfold\.(com|dev)/, "");
};

export const RichText = ({
  p = null,
  elements = PRISMIC_ELEMENTS,
  render,
  ...rest
}) => {
  const serializer = useMemo(
    () => (type, element, content, children, key) => {
      if (p && type === Elements.paragraph) {
        return React.createElement(p, { ...rest, key }, children);
      }
      if (type === Elements.hyperlink) {
        return React.createElement(
          A,
          {
            href: stripPenfoldDomain(element.data.url),
            target: element.data.target || "_self",
            key,
          },
          children
        );
      }
      if (elements[type]) {
        return React.createElement(elements[type], { ...rest, key }, children);
      }

      return null;
    },
    [p, rest] // TODO: I think this is pointless as rest will always change
  );

  return (
    <PrismicRichText
      elements={elements}
      htmlSerializer={serializer}
      render={render}
    />
  );
};

export const PromoText = (props) => {
  return <RichText elements={PRISMIC_ELEMENTS} {...props} />;
};

export const PageTitle = (props: { title: RichTextBlock }) => {
  return <PromoText render={props.title} textAlign="center" mt="5rem" mb="0" />;
};
